import {
  createBrowserHistory,
  createMemoryHistory,
  History,
  Location,
  To,
} from "history";
import React from "react";

import { asyncTimeout } from "@use/async-timeout";
import { isNode } from "@use/is-node";
import { hrtime } from "@use/universal-hrtime";
import { clearTimeout, setTimeout } from "@use/universal-timeout";

import { HistoryContext } from "./HistoryContext";
import { PostRouteRender, PostRouteRenderProps } from "./PostRouteRender";
import { PreRouteRender, PreRouteRenderProps } from "./PreRouteRender";
import { Route, RouteProps } from "./Route";
import {
  RouteErrorBoundary,
  RouteErrorBoundaryProps,
} from "./RouteErrorBoundary";
import { RouteNotFound, RouteNotFoundProps } from "./RouteNotFound";

/**
 * Extract type of <Route .../> elements, so that we can safely perform type
 * checking even when using react-hot-loader.
 */
const typeOfRoute = (<Route />).type;

/**
 * Extract type of <RouteErrorBoundary .../> elements, so that we can safely
 * perform type checking even when using react-hot-loader.
 */
const typeOfRouteErrorBoundary = (<RouteErrorBoundary />).type;

/**
 * Extract type of <RouteNotFound .../> elements, so that we can safely perform
 * type checking even when using react-hot-loader.
 */
const typeOfRouteNotFound = (<RouteNotFound />).type;

/**
 * Extract type of <PreRouteRender .../> elements, so that we can safely perform
 * type checking even when using react-hot-loader.
 */
const typeOfPreRouteRender = (<PreRouteRender />).type;

/**
 * Extract type of <PostRouteRender .../> elements, so that we can safely
 * perform type checking even when using react-hot-loader.
 */
const typeOfPostRouteRender = (<PostRouteRender />).type;

/**
 * Specification of props required by the router
 */
export type RouterProps = {
  /**
   * If given, then all paths within this router will automatically be
   * prefixed with this address.
   *
   * This is very useful, if you're serving your application from a
   * sub-directory instead of the root of the domain.
   */
  basePath?: string;

  /**
   * Optionally supply an error thrown from outside the rendering context of
   * this router, but that should be handled by the <RouteErrorBoundary />.
   */
  err?: Error;

  /**
   * If set to true, the router will **not** render any routes, regardless of
   * the current path.
   *
   * This is a useful setting, if you'd like to defer rendering of content for
   * a while during application lifecycle initialization.
   */
  disabled?: boolean;

  /**
   * This prop allows us to inject an initial state into the router, which is
   * how we handle route resolving within the server side context.
   */
  initialState?: RouterState;

  /**
   * If an async route is unable to open instantly after being instantiated,
   * it will trigger the [[onRouteRequested]] callback.
   *
   * If this happens, you can set a minimum delay between triggering the
   * [[onRouteRequested]] and [[onRouteOpened]] callbacks. That way you can
   * make sure that any throbbers added to the ui will be displayed for at
   * least the given amount of time.
   *
   * By default this setting is disabled, so no minimum delay is enforced.
   */
  minOpenDelay?: number;

  /**
   * A callback that's triggered when the user navigates to a specified
   * route.
   */
  onRouteRequested?(path: string, history: History): void;

  /**
   * A callback that's triggered once the desired route has been rendered.
   */
  onRouteOpened?(path: string, history: History): void;

  children: React.ReactNode;
};

/**
 * Specification of the runtime state of the router
 */
export type RouterState = {
  /**
   * Will contain a reference to the content emitted by the currently matched
   * route.
   */
  content?: React.JSX.Element | null;

  /**
   * Will specify if an error was caught during rendering of the currently
   * matched route (in which case we should fall back to the defined error
   * boundary).
   */
  didCatchError?: boolean;

  /**
   * Will specify if a route (of any kind, including the error boundaries) has
   * been rendered. We're going to use this information to determine if we
   * should render the <PreRouteRender /> or <PostRouteRender /> content.
   */
  didRouteRender?: boolean;

  /**
   * Internal cache of the route that's matched by the currently opened route
   * (we use this to properly determine which of the <Route ... /> nodes to
   * swap with the rendered content).
   */
  matchedRoute?: React.ReactElement<RouteProps>;
};

/**
 * Specification of the format used when storing a collection of routes given
 * when mounting the router.
 */
type RouteCollection = {
  /**
   * A regular expression we can use to check if the route matches a given
   * path.
   */
  pattern: RegExp;

  /**
   * A collection of parameter names in the given path, which makes it easy to
   * convert values extracted from the [[pattern]] reg ex into an object
   * grouped by the parameter names.
   */
  params: string[];

  /**
   * Reference to the original <Route ... /> element, so that we can safely
   * identify it later on, when injecting rendered content.
   */
  node: React.ReactElement<RouteProps>;
}[];

/**
 * The router mounts the currently active component in the framework into your
 * React application.
 */
export class Router extends React.PureComponent<RouterProps, RouterState> {
  /**
   * Set up initial (blank) state, making sure that this.state is always
   * available during run time.
   */
  public readonly state: RouterState = this.props.initialState || {};

  /**
   * A history manager will be created when the component is created, so that
   * we can properly track navigation.
   */
  private readonly history: History = isNode()
    ? createMemoryHistory()
    : createBrowserHistory();

  /**
   * Internal cache of the path that's currently active within the given
   * history element.
   *
   * We're going to use this value to ensure that whatever content is being
   * emitted is always the up-to-date with the currently requested path.
   */
  private currentPath: string = this.history.location.pathname.replace(this.props.basePath ?? "", "") || "/";

  /**
   * Specifies if an error boundary has been defined, so that we can render
   * this component in case something goes wrong during rendering of the
   * current path.
   */
  private errorBoundary?: React.ReactElement<RouteErrorBoundaryProps>;

  /**
   * Specifies if a NotFound route has been defined, so that we can fall back
   * to this in case none of the defined routes match the current path.
   */
  private notFound?: React.ReactElement<RouteNotFoundProps>;

  /**
   * This will contain an internal cache of routes that are currently nested
   * within the router, so that we can easily search through them whenever a
   * navigation event occurs.
   */
  private routes: RouteCollection = [];

  /**
   * Will contain a reference to the unregister callback, which we can use to
   * stop listening for history events.
   */
  private unregisterHistoryListener?: () => void;

  /**
   * Cached reference to the previously rendered children, which we're going
   * to use to prevent setting up routes over and over again if children
   * haven't actually been changed!
   */
  private prevChildren?: React.ReactNode;

  /**
   * Cached reference to the previous error being propagated from parent into
   * the error boundary within this router - we're using this to avoid
   * re-rendering when it's not needed.
   */
  private prevErr?: Error;

  constructor(props: RouterProps) {
    super(props);

    if (!isNode()) {
      const orgPush = this.history.push;
      const orgReplace = this.history.replace;

      this.history.push = (to: To, state?: any) => {
        const prependedTo = typeof to === "string"
          ? `${props.basePath ?? ""}${to}`
          : {
            ...to,
            pathname: to.pathname !== undefined
              ? `${props.basePath ?? ""}${to}`
              : undefined,
          };

        orgPush.call(this.history, prependedTo, state);
      }
      this.history.replace = (to: To, state?: any) => {
        const prependedTo = typeof to === "string"
          ? `${props.basePath ?? ""}${to}`
          : {
            ...to,
            pathname: to.pathname !== undefined
              ? `${props.basePath ?? ""}${to}`
              : undefined,
          };

        orgReplace.call(this.history, prependedTo, state);
      }
    }
  }

  /**
   * Once the component has been mounted, we'll need to run through all given
   * routes and ensure that they are properly configured and then handle the
   * currently matched path!
   */
  public componentDidMount(): void {
    this.registerRoutes().catch((err) => {
      throw err;
    });

    // Register initial children
    this.prevChildren = this.props.children;
    this.prevErr = this.props.err;

    // Start listening for navigation events
    this.unregisterHistoryListener = this.history.listen(this.onNavigation);
  }

  /**
   * Whenever the component is updated, we'll need to re-configure routes and
   * check to see if anything has been changed (so that the new set of routes
   * will be properly handled).
   */
  public componentDidUpdate(): void {
    if (
      this.prevChildren !== this.props.children ||
      this.prevErr !== this.props.err
    ) {
      this.registerRoutes().catch((err) => {
        throw err;
      });
    }

    // Register current props, so we know if we'll need to update the
    // rendering on next update
    this.prevChildren = this.props.children;
    this.prevErr = this.props.err;
  }

  /**
   * Set up an error boundary so that we can properly fall back to the defined
   * <RouteErrorBoundary ... /> component.
   */
  public componentDidCatch(err: unknown): void {
    // If an error boundary has been supplied, then make sure that it's used
    // to catch the error
    if (
      this.errorBoundary &&
      this.errorBoundary.props.render &&
      !this.state.didCatchError
    ) {
      // Run the render method of our error boundary
      this.renderErrorBoundary(err).then(
        (content) => {
          // If we managed to render an error message, then update our
          // state accordingly so that it'll be displayed in the UI
          this.setState({
            content,
            didCatchError: true,
          });
        },
        (err2) => {
          // Otherwise propagate errors to the console
          throw err2;
        }
      );
    } else {
      // If no error boundary was available, then propagate the error so
      // it properly breaks rendering as would be expected normally
      throw err;
    }
  }

  /**
   * Unbind event listeners when the component is unmounted
   */
  public componentWillUnmount(): void {
    // Stop listening for navigation events
    if (this.unregisterHistoryListener) {
      this.unregisterHistoryListener();
    }

    // Clean up memory
    this.routes = [];
    this.unregisterHistoryListener = undefined;
  }

  /**
   * Simply render the last emitted content from the router.
   */
  public render(): React.JSX.Element {
    return (
      <HistoryContext.Provider value={this.history}>
        {recursivelyInjectContent({
          children: this.props.children,
          disabled: this.props.disabled,
          state: this.state,
        })}
      </HistoryContext.Provider>
    );
  }

  /**
   * Helper that renders the error boundary with the given error message if
   * something goes wrong during execution
   */
  private async renderErrorBoundary(err: unknown): Promise<React.JSX.Element | null> {
    // Bail out if no error boundary is available
    if (!this.errorBoundary || !this.errorBoundary.props.render) {
      return null;
    }

    return this.errorBoundary.props.render(this.currentPath, err);
  }

  /**
   * Helper that'll register the routes that are currently declared within the
   * context of our router, and then resolve the content of the currently
   * opened path.
   */
  private async registerRoutes(): Promise<void> {
    // Extract routes from children
    const { errorBoundary, notFound, routes } = await extractRoutes(
      this.props.children
    );

    // Update internal state
    this.errorBoundary = errorBoundary;
    this.notFound = notFound;
    this.routes = routes;

    // Trigger a fake navigation event, so that we'll resolve the current
    // path again based on the new route configuration
    await this.onNavigation({ location: this.history.location });
  }

  /**
   * Listener that's triggered as the user navigates around the site, making
   * sure that state of the router is kept up-to-date.
   */
  private readonly onNavigation = async ({ location }: { location: Location }) => {
    const path = location.pathname.replace(this.props.basePath ?? "", "") || "/";

    // Bail out if the router has been disabled
    if (this.props.disabled) {
      return;
    }

    // Update currently requested path
    this.currentPath = path;

    // Prepare variable to keep track of whether or not we're going to emit
    // a route requested event to subscribers?
    let emittedRouteRequested = false;

    // Set a timeout, which is going to emit onRouteRequested after 45ms
    // (allowing up to 3 dropped frames, which isn't truly noticeable to
    // users as transitions wont be running while waiting for rendering)
    const routeRequestedTimeout = setTimeout(() => {
      emittedRouteRequested = this.triggerRouteRequested(path);
    }, 45);

    // Register timestamp for when the route was initially requested!
    const routeRequestedAt = hrtime();

    // Generate new state for the router by resolving the given path, so
    // we know what to render
    let newState: RouterState | undefined;

    try {
      if (!this.props.err) {
        // Resolve the route based on the current state of the
        // application
        newState = await resolvePath({
          errorBoundary: this.errorBoundary,
          notFound: this.notFound,
          path,
          routes: this.routes,
        });
      } else {
        // If an error from the parent rendering context was provided,
        // then propagate it to the defined error boundary if possible
        if (!this.errorBoundary || !this.errorBoundary.props.render) {
          // If no error boundary is available, then we have no other
          // option but to throw the error
          throw this.props.err;
        }

        // If we do have an error boundary, then update state to reflect the
        // caught error
        newState = {
          content: await this.errorBoundary.props.render(path, this.props.err),
          matchedRoute: undefined,
        };
      }
    } finally {
      // Clear the timeout to trigger the onRouteRequested event if it
      // hasn't already happened (in that case the route is immediately
      // available - so we go straight for the onRouteOpened event
      // instead)
      clearTimeout(routeRequestedTimeout);

      // If the route didn't render immediately, then we'll need to wait
      // the defined minimum delay
      if (emittedRouteRequested && this.props.minOpenDelay) {
        await asyncTimeout(
          this.props.minOpenDelay - (hrtime() - routeRequestedAt)
        );
      }

      // Once we get here, verify that the path we just resolved is still
      // the currently active one (if not, then bail out!)
      if (path === this.currentPath && newState) {
        // Trigger the onRouteOpened callback
        this.triggerRouteOpened(path);

        // Render by updating state
        this.setState({
          ...newState,
          didRouteRender: true,
        });
      }
    }
  };

  /**
   * Helper that'll trigger the onRouteRequested callback (if any was given).
   */
  private readonly triggerRouteRequested = (path: string) => {
    // Bail out if no callback has been given
    if (!this.props.onRouteRequested) {
      return false;
    }

    try {
      this.props.onRouteRequested(path, this.history);
    } catch (err) {
      // Notify developers of any errors occurring during their
      // registerred callback
      console.error(err); // tslint:disable-line no-console
    }

    // Let the caller know that an event was triggered
    return true;
  };

  /**
   * Helper that'll trigger the onRouteOpened callback (if any was given).
   */
  private readonly triggerRouteOpened = (path: string) => {
    // Bail out if no callback has been given
    if (!this.props.onRouteOpened) {
      return false;
    }

    try {
      this.props.onRouteOpened(path, this.history);
    } catch (err) {
      // Notify developers of any errors occurring during their
      // registerred callback
      console.error(err); // tslint:disable-line no-console
    }

    // Let the caller know that an event was triggered
    return true;
  };
}

/**
 * Internal helper that'll iterate over all nested <Route ... /> elements,
 * and make sure that they're properly handled when the user navigates
 * around.
 */
async function extractRoutes(children?: React.ReactNode): Promise<{
  errorBoundary?: React.ReactElement<RouteErrorBoundaryProps>;
  notFound?: React.ReactElement<RouteNotFoundProps>;
  routes: RouteCollection;
}> {
  // Start by recursively extracting a list of routes defined within this
  // router
  const routeNodes = recursivelyGetRoutes(children);

  // Prepare output variables
  let errorBoundary: React.ReactElement<RouteErrorBoundaryProps> | undefined;
  let notFound: React.ReactElement<RouteNotFoundProps> | undefined;
  const routes: RouteCollection = [];

  // Iterate over the nested routes, and update cached data, so that it's
  // available during route matching
  for (const route of routeNodes) {
    // Register if an error boundary was defined
    if (isRouteErrorBoundary(route)) {
      errorBoundary = route;

      continue;
    }

    // Register if an not found route was defined
    if (isRouteNotFound(route)) {
      notFound = route;

      continue;
    }

    // ... Otherwise push the route to the list of nested routes, so we
    // can match it later on
    if (isRoute(route) && route.props.path) {
      routes.push({
        // Extract parameter names (e.g. /user/:id)
        params: (route.props.path.match(/(\:|\*)[^\/\.]+/gi) || [])
          .slice(0)
          .map((param) => param.substr(1)),

        // Prepare regular expression to match routes including
        // parameter wildcards
        pattern: new RegExp(
          `^${route.props.path
            .replace(/\:[^\/\.]+/gi, "([^/.]+)")
            .replace(/\*[^\/\.]+/gi, "(.*)")}$`,
          "i"
        ),

        // Pass reference to the original route node
        node: route,
      });
    }
  }

  // Return output
  return { errorBoundary, notFound, routes };
}

/**
 * Helper that'll recursively iterate over all children in the render tree, and
 * extract a collection of <Route ... /> elements, so that we can safely add
 * their paths to our router.
 */
function recursivelyGetRoutes(
  children: React.ReactNode
): React.ReactElement<{}>[] {
  // Convert children to an array, which we can safely iterate over
  const childArray = React.Children.toArray(children);

  // Prepare output
  let routes: React.ReactElement<{}>[] = [];

  // Loop through all children and extract only routes
  for (const child of childArray) {
    // Skip primitive children, they cannot be routes...
    if (!React.isValidElement<React.PropsWithChildren<{}>>(child)) {
      continue;
    }

    // ... Otherwise determine how to handle the child
    switch (child.type) {
      // Push children of our route types
      case typeOfRoute:
      case typeOfRouteErrorBoundary:
      case typeOfRouteNotFound:
        routes.push(child);
        break;

      // Recursively parse any other kind of children
      default:
        routes = [
          ...routes,
          ...recursivelyGetRoutes(child.props.children), // tslint:disable-line no-unsafe-any
        ];
    }
  }

  return routes;
}

/**
 * Helper that recursively finds all content mounted inside the router as well
 * as content emitted by the router.
 */
function recursivelyInjectContent({
  children,
  disabled,
  state,
}: {
  children?: React.ReactNode;
  disabled?: boolean;
  state: RouterState;
}): React.ReactNode[] {
  // Loop through all children and map their output according to the routes
  // contained within them!
  return React.Children.toArray(children).map((child, index) => {
    // Push simple type children
    if (!React.isValidElement<React.PropsWithChildren<{}>>(child)) {
      return child;
    }

    // Handle <Route ... /> tags nested within the render tree; here
    // we'll need to determine if the route is matching the currently
    // opened one, and in that case emit the currently rendered content
    if (isRoute(child)) {
      return !disabled ? possiblyInjectRoute(child, state) : null;
    }

    // Handle <RouteNotFound ... /> tags nested within the render tree;
    // here we'll need to verify that the current path wasn't found, and
    // in that case inject the currently rendered content into this
    // route
    if (isRouteNotFound(child)) {
      return !disabled ? possiblyInjectRouteNotFound(state) : null;
    }

    // Handle <RouteErrorBoundary ... /> tags nested within the render
    // tree; at this point we simply need to verify if an error occurred
    // while attempting to render the current route
    if (isRouteErrorBoundary(child)) {
      return !disabled ? possiblyInjectRouteErrorBoundary(state) : null;
    }

    // Handle <PreRouteRender> elements by injecting them only if no
    // content has been emitted yet
    if (isPreRouteRender(child)) {
      return React.cloneElement(child, {
        children: recursivelyInjectContent({
          children: child.props.children,
          disabled,
          state,
        }),
        show: !state.didRouteRender,
      });
    }

    // Handle <PostRotueRender> elements by injecting them only if a
    // route has been rendered
    if (isPostRouteRender(child)) {
      return React.cloneElement(child, {
        children: recursivelyInjectContent({
          children: child.props.children,
          disabled,
          state,
        }),
        show: state.didRouteRender,
      });
    }

    // ... If we get here, that means this is a plain ol' React node,
    // which we should simply inject into the render tree - however, we
    // will need to inject content recursively
    return React.cloneElement(child, {
      // tslint:disable-next-line no-unsafe-any
      children: recursivelyInjectContent({
        children: (child.props as { children: React.ReactNode }).children,
        disabled,
        state,
      }),
      key: child.key || index,
    });
  });
}

/**
 * Helper that'll possibly inject the currently rendered content in place of the
 * given <Route ... /> element, if it's the one that's currently matched by the
 * router.
 */
function possiblyInjectRoute(
  route: React.ReactElement<RouteProps>,
  state: RouterState
): React.ReactNode {
  // Bail out if the router is inactive or this is not the currently
  // matched route...
  if (
    state.didCatchError ||
    !state.matchedRoute ||
    state.matchedRoute.props.path !== route.props.path ||
    state.matchedRoute.props.render !== route.props.render
  ) {
    return;
  }

  // ... Otherwise return the currently rendered content
  if (!state.content) {
    return null;
  }

  return state.content.key
    ? state.content
    : React.cloneElement(state.content, {
        key: state.matchedRoute.key || state.matchedRoute.props.path,
      });
}

/**
 * Helper that'll possibly inject the currently rendered content in place of
 * the given <RouteNotFound ... /> element, if none of the <Route ... />
 * elements were actually matched.
 */
function possiblyInjectRouteNotFound(state: RouterState): React.ReactNode {
  // Bail out if the router is disabled, or if another route has actually
  // been matched
  if (state.didCatchError || state.matchedRoute) {
    return;
  }

  // ... Otherwise inject the currently rendered content here
  if (!state.content) {
    return null;
  }

  return state.content.key
    ? state.content
    : React.cloneElement(state.content, { key: "notFound" });
}

/**
 * Helper that'll possibly inject the currently rendered content in place of
 * the given <RouteErrorBoundary ... /> element, if an error has been caught
 * at some point during the render process.
 */
function possiblyInjectRouteErrorBoundary(state: RouterState): React.ReactNode {
  // Bail out if the router is disabled, or if we haven't actually caught
  // any errors during rendering...
  if (!state.didCatchError) {
    return;
  }

  // ... Otherwise inject the currently rendered content here
  if (!state.content) {
    return null;
  }

  return state.content.key
    ? state.content
    : React.cloneElement(state.content, { key: "errorBoundary" });
}

/**
 * Callback that'll resolve the given path up against the given list of
 * routes, and return a fresh state which we should use to render the
 * component.
 */
async function resolvePath({
  errorBoundary,
  notFound,
  path,
  routes,
}: {
  errorBoundary?: React.ReactElement<RouteErrorBoundaryProps>;
  notFound?: React.ReactElement<RouteNotFoundProps>;
  path: string;
  routes: RouteCollection;
}): Promise<RouterState> {
  // Prepare a "fresh" state object for our router, which we're going to
  // use when finally applying the new state
  const newState: RouterState = {
    content: undefined,
    didCatchError: undefined,
    matchedRoute: undefined,
  };

  try {
    // Iterate over the list of nested routes, and check to see if any
    // of them match the current path
    for (const route of routes) {
      const match = path.match(route.pattern);

      // If the route isn't a match, then we're simply gonna skip
      // ahead...
      if (!match || !route.node.props.render) {
        continue;
      }

      // If we get here, the route is a match - generate an object
      // hash containing all wildcards matched by the route pattern
      const params: { [key: string]: string } = {};

      match.slice(1).forEach((value, index) => {
        params[route.params[index]] = value;
      });

      // Render the content of this route
      newState.content = await route.node.props.render(path, params);

      // If the route actually rendered some content, then break out
      // of this loop and simply render this route
      if (newState.content) {
        // Register the matched route, so we know where to inject
        // the rendered content
        newState.matchedRoute = route.node;

        break;
      }
    }

    // If we get here, without identifying any new content to render,
    // then check if a notFound route was given
    if (!newState.content && notFound && notFound.props.render) {
      newState.content = await notFound.props.render(path);
    }

    // Now... If we get here, and there still isn't any content then
    // throw an error to fall into our error boundary if defined
    if (!newState.content) {
      throw new Error(
        "<Router />.resolveLocation(): No route matched the current location."
      );
    }
  } catch (err) {
    // If something goes wrong, then check if we actually have an error
    // boundary defined?
    if (!errorBoundary || !errorBoundary.props.render) {
      // If not, then propagate the error, so that it'll actually
      // break the render chain
      throw err;
    }

    // If we do have an error boundary, then update state to reflect the
    // caught error
    newState.didCatchError = true;
    newState.content = await errorBoundary.props.render(path, err as unknown);
  }

  // Return the resolved state
  return newState;
}

/**
 * Typeguard that easily lets us determine if an element is an instance of the
 * <Route ... /> component.
 */
function isRoute(
  node: React.ReactElement<{}>
): node is React.ReactElement<RouteProps> {
  return node.type === typeOfRoute;
}

/**
 * Typeguard that easily lets us determine if an element is an instance of the
 * <RouteErrorBoundary ... /> component.
 */
function isRouteErrorBoundary(
  node: React.ReactElement<{}>
): node is React.ReactElement<RouteErrorBoundaryProps> {
  return node.type === typeOfRouteErrorBoundary;
}

/**
 * Typeguard that easily lets us determine if an element is an instance of the
 * <RouteNotFound ... /> component.
 */
function isRouteNotFound(
  node: React.ReactElement<{}>
): node is React.ReactElement<RouteNotFoundProps> {
  return node.type === typeOfRouteNotFound;
}

/**
 * Typeguard that easily lets us determine if an element is an instance of the
 * <PostRouteRender ... /> component.
 */
function isPostRouteRender(
  node: React.ReactElement<{}>
): node is React.ReactElement<
  PostRouteRenderProps & { children: React.ReactNode[] }
> {
  return node.type === typeOfPostRouteRender;
}

/**
 * Typeguard that easily lets us determine if an element is an instance of the
 * <PreRouteRender ... /> component.
 */
function isPreRouteRender(
  node: React.ReactElement<{}>
): node is React.ReactElement<
  PreRouteRenderProps & { children: React.ReactNode[] }
> {
  return node.type === typeOfPreRouteRender;
}
